import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLanguage } from '@/hooks/use-language';
import { supabase } from '@/integrations/supabase/client';
import { CommentInput } from './CommentInput';
import { CommentItem } from './CommentItem';
import { MessageCircle } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

interface Comment {
  id: string;
  content: string;
  created_at: string;
  is_edited: boolean;
  user_id: string;
  user_first_name?: string;
  user_last_name?: string;
  parent_comment_id?: string | null;
  parent_user_first_name?: string;
  parent_user_last_name?: string;
}

interface EventCommentsProps {
  eventId: string;
}

export function EventComments({ eventId }: EventCommentsProps) {
  const { t } = useLanguage();
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [replyTo, setReplyTo] = useState<{ commentId: string; userName: string } | null>(null);

  const organizeComments = (comments: Comment[]) => {
    const commentMap = new Map<string, Comment & { replies: Comment[] }>();
    const topLevelComments: (Comment & { replies: Comment[] })[] = [];

    // First pass: create map and initialize replies array
    comments.forEach(comment => {
      commentMap.set(comment.id, { ...comment, replies: [] });
    });

    // Second pass: organize into hierarchy
    comments.forEach(comment => {
      const commentWithReplies = commentMap.get(comment.id)!;
      
      if (comment.parent_comment_id) {
        // This is a reply - add to parent's replies array
        const parent = commentMap.get(comment.parent_comment_id);
        if (parent) {
          parent.replies.push(commentWithReplies);
        } else {
          // Parent not found (orphaned reply) - show as top-level
          topLevelComments.push(commentWithReplies);
        }
      } else {
        // Top-level comment
        topLevelComments.push(commentWithReplies);
      }
    });

    return topLevelComments;
  };

  const fetchComments = async () => {
    try {
      const { data, error } = await supabase
        .from('event_comments')
        .select(`
          id,
          content,
          created_at,
          is_edited,
          user_id,
          parent_comment_id
        `)
        .eq('event_id', eventId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Fetch user profiles for each comment and parent comments
      const userIds = [...new Set(data?.map(c => c.user_id) || [])];
      const parentCommentIds = [...new Set(data?.map(c => c.parent_comment_id).filter(Boolean) || [])];
      
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, first_name, last_name')
        .in('user_id', userIds);

      // Fetch parent comment info
      const { data: parentComments } = parentCommentIds.length > 0 
        ? await supabase
            .from('event_comments')
            .select('id, user_id')
            .in('id', parentCommentIds)
        : { data: [] };

      const parentUserIds = [...new Set(parentComments?.map(c => c.user_id) || [])];
      const { data: parentProfiles } = parentUserIds.length > 0
        ? await supabase
            .from('profiles')
            .select('user_id, first_name, last_name')
            .in('user_id', parentUserIds)
        : { data: [] };

      type Profile = { user_id: string; first_name: string; last_name: string };
      type ParentComment = { id: string; user_id: string };

      const profileMap = new Map<string, Profile>(
        profiles?.map(p => [p.user_id, p] as [string, Profile]) || []
      );

      const parentProfileMap = new Map<string, Profile>(
        parentProfiles?.map(p => [p.user_id, p] as [string, Profile]) || []
      );

      const parentCommentMap = new Map<string, ParentComment>(
        parentComments?.map(c => [c.id, c] as [string, ParentComment]) || []
      );

      const commentsWithUsers = (data || []).map(comment => {
        const parentComment = comment.parent_comment_id 
          ? parentCommentMap.get(comment.parent_comment_id)
          : null;
        const parentProfile = parentComment 
          ? parentProfileMap.get(parentComment.user_id)
          : null;

        return {
          ...comment,
          user_first_name: profileMap.get(comment.user_id)?.first_name,
          user_last_name: profileMap.get(comment.user_id)?.last_name,
          parent_user_first_name: parentProfile?.first_name,
          parent_user_last_name: parentProfile?.last_name,
        };
      });

      setComments(commentsWithUsers);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();

    // Subscribe to real-time updates
    const channel = supabase
      .channel(`event-comments-${eventId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'event_comments',
          filter: `event_id=eq.${eventId}`
        },
        () => {
          fetchComments();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [eventId]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageCircle className="w-5 h-5" />
          {t('event.comments.title')}
          {comments.length > 0 && (
            <span className="text-sm font-normal text-muted-foreground">
              ({comments.length})
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center text-muted-foreground py-8">
            {t('common.loading') || 'Loading...'}
          </div>
        ) : (
          <>
            {comments.length === 0 ? (
              <div className="text-center text-muted-foreground py-8">
                {t('event.comments.empty')}
              </div>
            ) : (
              <div className="space-y-1">
                {organizeComments(comments).map((comment, index, array) => (
                  <div key={comment.id}>
                    {/* Render parent comment */}
                    <CommentItem 
                      comment={comment} 
                      onDelete={fetchComments}
                      onReply={(commentId, userName) => {
                        setReplyTo({ commentId, userName });
                      }}
                    />
                    
                    {/* Render replies directly under parent */}
                    {comment.replies.length > 0 && (
                      <div className="ml-8 space-y-1 mt-1">
                        {comment.replies.map((reply, replyIndex) => (
                          <div key={reply.id}>
                            {replyIndex > 0 && <Separator />}
                            <CommentItem 
                              comment={reply} 
                              onDelete={fetchComments}
                              onReply={(commentId, userName) => {
                                setReplyTo({ commentId, userName });
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {index < array.length - 1 && <Separator />}
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 pt-6 border-t">
              <CommentInput 
                eventId={eventId} 
                onCommentAdded={fetchComments}
                replyTo={replyTo}
                onCancelReply={() => setReplyTo(null)}
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
